<template>
  <div :class="readOnly && 'lib__visible-print'" class="lib__form__main" data-cy="resume-component">
    <ValidationObserver ref="validator" slim>
      <div class="lib__form__main__content">
        <header class="lib__form__main__header">
          <h2 class="lib__h2">{{ $t('resume.title') }}</h2>
        </header>

        <resume-section-title minimumBadgeWidth :read-only="readOnly" :step="1" @goToStep="goToStep($event)">
          <template v-slot:icon>1</template>
          <template>{{ $t('resume.address') }}</template>
        </resume-section-title>
        <resume-container commonMobileAndDesktopLayout>
          <template v-slot:common>
            <resume-address :address="addressState"></resume-address>
          </template>
        </resume-container>

        <resume-section-title minimumBadgeWidth noMarginTop :read-only="readOnly" :step="2"
          @goToStep="goToStep($event)">
          <template v-slot:icon>2</template>
          <template>{{ $t('resume.request') }}</template>
        </resume-section-title>
        <resume-container commonMobileAndDesktopLayout>
          <template v-slot:common>
            <div class="lib__infobox-list">
              <template v-if="configurationInfoState && configurationInfoState.changeType === 0">
                <info-box item>
                  <template>
                    <p>{{ $t('synergrid.config.synergridRef') }} :</p>
                    <span>{{ configurationInfoState && configurationInfoState.newRequestInfo && configurationInfoState.newRequestInfo.synergridRef }}</span>
                  </template>
                </info-box>
                <info-box item>
                  <template>
                    <p>{{ $t('synergrid.config.group') }} :</p>
                    <span>{{ configurationInfoState && configurationInfoState.newRequestInfo && configurationInfoState.newRequestInfo.group }}</span>
                  </template>
                </info-box>
                <info-box item>
                  <template>
                    <p>{{ $t('synergrid.config.power') }} :</p>
                    <span>{{ configurationInfoState && configurationInfoState.newRequestInfo && configurationInfoState.newRequestInfo.power }} Kw</span>
                  </template>
                </info-box>
              </template>
              <info-box item v-else-if="configurationInfoState && configurationInfoState.changeType === 1">
                <template>
                  <p>{{ $t('synergrid.config.ean') }} :</p>
                  <span>{{ configurationInfoState && configurationInfoState.moveRequestInfo && configurationInfoState.moveRequestInfo.ean }}</span>
                </template>
              </info-box>
              <template v-else-if="configurationInfoState && configurationInfoState.changeType === 2">
                <info-box item>
                  <template>
                    <p>{{ $t('synergrid.config.group') }} :</p>
                    <span>{{ configurationInfoState && configurationInfoState.enhanceRequestInfo && configurationInfoState.enhanceRequestInfo.group }}</span>
                  </template>
                </info-box>
                <info-box item>
                  <template>
                    <p>{{ $t('synergrid.config.power') }} :</p>
                    <span>{{ configurationInfoState && configurationInfoState.enhanceRequestInfo && configurationInfoState.enhanceRequestInfo.power }}</span>
                  </template>
                </info-box>
              </template>
              <info-box item v-else-if="configurationInfoState && configurationInfoState.changeType === 3">
                <template>
                  <p>{{ $t('synergrid.config.ean') }} :</p>
                  <span>{{ configurationInfoState && configurationInfoState.removeRequestInfo && configurationInfoState.removeRequestInfo.ean }}</span>
                </template>
              </info-box>
              <template v-else-if="configurationInfoState && configurationInfoState.changeType === 4">
                <info-box item>
                  <template>
                    <p>{{ $t('synergrid.config.oldRef') }} :</p>
                    <span>{{ configurationInfoState && configurationInfoState.modifyRefRequestInfo && configurationInfoState.modifyRefRequestInfo.oldRef }}</span>
                  </template>
                </info-box>
                <info-box item>
                  <template>
                    <p>{{ $t('synergrid.config.oldGroup') }} :</p>
                    <span>{{ configurationInfoState && configurationInfoState.modifyRefRequestInfo && configurationInfoState.modifyRefRequestInfo.oldGroup }}</span>
                  </template>
                </info-box>
                <info-box item>
                  <template>
                    <p>{{ $t('synergrid.config.newRef') }} :</p>
                    <span>{{ configurationInfoState && configurationInfoState.modifyRefRequestInfo && configurationInfoState.modifyRefRequestInfo.newRef }}</span>
                  </template>
                </info-box>
                <info-box item>
                  <template>
                    <p>{{ $t('synergrid.config.newGroup') }} :</p>
                    <span>{{ configurationInfoState && configurationInfoState.modifyRefRequestInfo && configurationInfoState.modifyRefRequestInfo.newGroup }}</span>
                  </template>
                </info-box>
              </template>

              <info-box item>
                <template>
                  <p>{{ $t('synergrid.config.wantedDates') }}</p>
                  <span>{{ month }} {{ configurationInfoState && configurationInfoState.year }}</span>
                </template>
              </info-box>
              <info-box :annexe-class="hasAttachedDocuments ? 'lib__text-success' : 'lib__text-danger'" item>
                <template>
                  <h3>{{ $t('resume.attachedDocuments') }}</h3>
                </template>
                <template v-slot:annexe>
                  <i :class="hasAttachedDocuments ? 'fa-check' : 'fa-times'" class="fas"></i>
                </template>
              </info-box>
            </div>
          </template>
        </resume-container>

        <resume-section-title minimumBadgeWidth noMarginTop :read-only="readOnly" :step="3"
          @goToStep="goToStep($event)">
          <template v-slot:icon>3</template>
          <template>{{ $t('resume.contact') }}</template>
        </resume-section-title>
        <resume-container commonMobileAndDesktopLayout>
          <template v-slot:common>
            <resume-contact-info :address="addressState" :contact-info="contactInfoState"></resume-contact-info>
          </template>
        </resume-container>

        <resume-section-title minimumBadgeWidth noMarginTop :read-only="readOnly" :step="4"
          @goToStep="goToStep($event)">
          <template v-slot:icon>4</template>
          <template>{{ $t('resume.vatInfo') }}</template>
        </resume-section-title>
        <resume-container borderless commonMobileAndDesktopLayout>
          <template v-slot:common>
            <info-box v-if="contactInfoState && contactInfoState.contactType === 'individual'">
              <template>
                <div>{{ $t('resume.buildingType') }}</div>
                <div>{{ buildingHasMoreThanTenYears ? $t('resume.buildingMoreThanTenYears') :
                  $t('resume.buildingLessThanTenYears') }}</div>
                <div v-if="buildingHasMoreThanTenYears">{{ $t('resume.vatRate6%') }}</div>
              </template>
            </info-box>

            <info-box v-if="contactInfoState && contactInfoState.contactType === 'freelance'">
              <template>
                <div>{{ freelanceWithDeclaration ? $t('vatInfo.withDeclarationDescription') :
                  $t('vatInfo.withoutDeclarationDescription') }}</div>
              </template>
            </info-box>

            <info-box v-if="contactInfoState && contactInfoState.contactType === 'company'">
              <template>
                <div>{{ firstStepCompanyVatInfo }}</div>
                <div>{{ secondStepCompanyVatInfo }}</div>
              </template>
            </info-box>
          </template>
        </resume-container>
        <Recaptcha :site-key='siteKey' :by-pass="$testMode" v-if="!readOnly" v-model="recaptcha" />
      </div>
    </ValidationObserver>
  </div>
</template>

<script lang="ts">
import { Component, Prop } from 'vue-property-decorator'
import Step from '@/components/steps/Step'
import PriceAnnexe from '@/components/display/PriceAnnexe.vue'
import ElectricityPriceCalculator from '@/components/display/ElectricityPriceCalculator.vue'
import ConstructionPriceCalculator from '@/components/display/ConstructionPriceCalculator.vue'
import GasPriceCalculator from '@/components/display/GasPriceCalculator.vue'
import ResumeContactInfo from '@/components/display/Resume/ResumeContactInfo.vue'
import ResumeVatInfo from '@/components/display/Resume/ResumeVatInfo.vue'
import ResumeAddress from '@/components/display/Resume/ResumeAddress.vue'
import {
  AddressPoint,
  ContactInfo,
  IndividualVatInfoSelection,
  FreelanceVatInfoSelection,
  CompanyVatInfoSelection,
  CompanyVatInfoFirstStepSelection,
  CompanyVatInfoSecondStepSelection,
  SynergridConfigurationInfo
} from '@/models'
import { State } from 'vuex-class'
import { contactService, calendarService } from '@/services'
import { FileInfo } from '@ores/vue-library'

@Component({
  name: 'summary-step',
  components: {
    PriceAnnexe,
    ElectricityPriceCalculator,
    GasPriceCalculator,
    ConstructionPriceCalculator,
    ResumeContactInfo,
    ResumeVatInfo,
    ResumeAddress,
  }
})
export default class SummaryStep extends Step {
  //#region [Property]
  @Prop({ type: Boolean, default: false }) public readonly readOnly!: boolean
  //#endregion

  //#region [Data]
  @State('requestId', { namespace: 'synergridConnection' })
  public requestId!: string
  @State('address', { namespace: 'synergridConnection/address' })
  public addressState!: AddressPoint | null
  @State('configurationInfo', { namespace: 'synergridConnection/configurationInfo' })
  public configurationInfoState!: SynergridConfigurationInfo | null
  @State('attachDocuments', { namespace: 'synergridConnection/attachment' })
  public attachDocumentsState!: boolean
  @State('locationFilesInfo', { namespace: 'synergridConnection/attachment' })
  public locationFilesInfoState!: FileInfo[]
  @State('contactInfo', { namespace: 'synergridConnection/contactInfo' })
  public contactInfoState!: ContactInfo | null
  @State('individualVatInfoSelection', { namespace: 'synergridConnection/vatInfo' })
  public individualVatInfoSelectionState!: IndividualVatInfoSelection | null
  @State('freelanceVatInfoSelection', { namespace: 'synergridConnection/vatInfo' })
  public freelanceVatInfoSelectionState!: FreelanceVatInfoSelection | null
  @State('companyVatInfoSelection', { namespace: 'synergridConnection/vatInfo' })
  public companyVatInfoSelectionState!: CompanyVatInfoSelection | null

  public recaptcha: string | null = null
  //#endregion

  //#region [Computed]
  public get siteKey() {
    return process.env.VUE_APP_CAPTCHA_SITE_KEY
  }
  public get buildingHasMoreThanTenYears(): boolean {
    return (
      this.individualVatInfoSelectionState !== null &&
      this.individualVatInfoSelectionState === IndividualVatInfoSelection.MoreThanTenYears
    )
  }

  public get freelanceWithDeclaration(): boolean {
    return this.freelanceVatInfoSelectionState === FreelanceVatInfoSelection.WithDeclaration
  }

  public get firstStepCompanyVatInfo(): string | undefined {
    if (this.companyVatInfoSelectionState) {
      switch (this.companyVatInfoSelectionState.primaryInfo) {
        case CompanyVatInfoFirstStepSelection.SubjectToVat:
          return this.$t('vatInfo.subjectToVatDescription') as string

        case CompanyVatInfoFirstStepSelection.TownWithoutSocialCharacter:
          return this.$t('vatInfo.townWithoutSocialCharacterDescription') as string

        case CompanyVatInfoFirstStepSelection.SocialHousing:
          return this.$t('vatInfo.socialHousingDescription') as string

        case CompanyVatInfoFirstStepSelection.Embassy:
          return this.$t('vatInfo.embassyDescription') as string

        case CompanyVatInfoFirstStepSelection.TownWithSocialCharacter:
          return this.$t('vatInfo.townWithSocialCharacterDescription') as string

        case CompanyVatInfoFirstStepSelection.HousingForDisabled:
          return this.$t('vatInfo.housingForDisabledDescription') as string

        case CompanyVatInfoFirstStepSelection.RestHouse:
          return this.$t('vatInfo.restHouseDescription') as string

        case CompanyVatInfoFirstStepSelection.School:
          return this.$t('vatInfo.schoolDescription') as string

        default:
          break
      }
    }
    return ''
  }

  public get secondStepCompanyVatInfo(): string | undefined {
    if (this.companyVatInfoSelectionState) {
      switch (this.companyVatInfoSelectionState.secondaryInfo) {
        case CompanyVatInfoSecondStepSelection.WithDeclaration:
          return this.$t('vatInfo.withDeclarationDescription') as string

        case CompanyVatInfoSecondStepSelection.WithoutDeclaration:
          return this.$t('vatInfo.withoutDeclarationDescription') as string

        case CompanyVatInfoSecondStepSelection.PublicBuilding:
          return this.$t('vatInfo.publicBuildingDescription') as string

        case CompanyVatInfoSecondStepSelection.PrivateBuildingLessThanTenYears:
          return this.$t('vatInfo.privateBuildingLessThanTenYearsDescription') as string

        case CompanyVatInfoSecondStepSelection.PrivateBuildingMoreThanTenYears:
          return this.$t('vatInfo.privateBuildingMoreThanTenYearsDescription') as string

        case CompanyVatInfoSecondStepSelection.SocialPrivateBuilding:
          return this.$t('vatInfo.socialPrivateBuildingDescription') as string

        case CompanyVatInfoSecondStepSelection.PrivateBuildingForDisabled:
          return this.$t('vatInfo.privateBuildingForDisabledDescription') as string

        default:
          break
      }
    }
    return ''
  }

  public get hasAttachedDocuments(): boolean {
    return (
      this.attachDocumentsState !== null &&
      this.attachDocumentsState &&
      this.locationFilesInfoState &&
      this.locationFilesInfoState.length > 0
    )
  }

  public get contactName(): string {
    if (this.contactInfoState) {
      switch (this.contactInfoState.contactType) {
        case 'individual':
          if (this.contactInfoState.contactIndividual) {
            let civility = null
            if (this.contactInfoState.contactIndividual.civility === 1) {
              civility = this.$t('contact.civility.mr')
            } else if (this.contactInfoState.contactIndividual.civility === 2) {
              civility = this.$t('contact.civility.mrs')
            }
            return `${civility} ${this.contactInfoState.contactIndividual.firstname} ${this.contactInfoState.contactIndividual.lastname}`
          }
          break
        case 'freelance':
          if (this.contactInfoState.contactFreelance) {
            let civility = null
            if (this.contactInfoState.contactFreelance.civility === 1) {
              civility = this.$t('contact.civility.mr')
            } else if (this.contactInfoState.contactFreelance.civility === 2) {
              civility = this.$t('contact.civility.mrs')
            }
            return `${civility} ${this.contactInfoState.contactFreelance.firstname} ${this.contactInfoState.contactFreelance.lastname}`
          }
          break
        case 'company':
          if (this.contactInfoState.contactCompany) {
            return `${this.contactInfoState.contactCompany.firstname} ${this.contactInfoState.contactCompany.lastname}`
          }
          break
        default:
          return ''
      }
    }
    return ''
  }

  public get contactMail(): string {
    if (this.contactInfoState) {
      switch (this.contactInfoState.contactType) {
        case 'individual':
          if (this.contactInfoState.contactIndividual) {
            return this.contactInfoState.contactIndividual.email!
          }
          break
        case 'freelance':
          if (this.contactInfoState.contactFreelance) {
            return this.contactInfoState.contactFreelance.email!
          }
          break
        case 'company':
          if (this.contactInfoState.contactCompany) {
            return this.contactInfoState.contactCompany.email!
          }
          break
        default:
          return ''
      }
    }
    return ''
  }

  public get contactPhones(): string[] {
    if (this.contactInfoState) {
      switch (this.contactInfoState.contactType) {
        case 'individual':
          if (this.contactInfoState.contactIndividual) {
            return [this.contactInfoState.contactIndividual.mobile, this.contactInfoState.contactIndividual.phone]
              .filter((n) => !!n)
              .map((n) => n!)
          }
          break
        case 'freelance':
          if (this.contactInfoState.contactFreelance) {
            return [this.contactInfoState.contactFreelance.mobile, this.contactInfoState.contactFreelance.phone]
              .filter((n) => !!n)
              .map((n) => n!)
          }
          break
        case 'company':
          if (this.contactInfoState.contactCompany) {
            return [this.contactInfoState.contactCompany.phone!]
          }
          break
        default:
          return []
      }
    }
    return []
  }

  public get contactAddress(): string {
    if (this.contactInfoState) {
      if (this.contactInfoState.useWorksAddress) {
        if (this.contactInfoState.contactType === 'company') {
          const address = this.contactInfoState.contactCompany!.address
          return `${address.street} ${address.streetNumber} ${address.streetBox || ''} ${address.zipCode} ${address.locality
            }`
        } else if (this.addressState) {
          return this.addressState.isMapLocation
            ? `${this.addressState.mapLocation!.lat} ${this.addressState.mapLocation!.lng} ${this.addressState.subCity.zipCode
            } ${this.addressState.subCity.cityName}`
            : `${this.addressState.streetSubCity!.name} ${this.addressState.houseNumber} ${this.addressState.subCity.zipCode
            } ${this.addressState.subCity.cityName}`
        }
      } else if (this.contactInfoState.otherAddress) {
        return `${this.contactInfoState.otherAddress.street} ${this.contactInfoState.otherAddress.streetNumber} ${this.contactInfoState.otherAddress.zipCode} ${this.contactInfoState.otherAddress.locality} ${this.contactInfoState.otherAddress.country}`
      }
    }
    return ''
  }

  public get month(): string | undefined {
    const m = this.configurationInfoState!.month
    if (m != null && m >= 1 && m <= 12) {
      return this.$t('month.' + calendarService.months[m - 1]) as string
    }

    return ''
  }
  //#endregion

  //#region [Method]
  public goToStep(step: number) {
    this.$emit('goToStep', step)
  }

  public async save(next: boolean, valid: boolean): Promise<boolean> {
    if (!next || !valid) {
      return true
    }

    if (!this.recaptcha) {
      throw new Error('No recaptcha token found')
    }

    switch (this.configurationInfoState!.changeType) {
      case 0:
        this.configurationInfoState!.moveRequestInfo = null
        this.configurationInfoState!.enhanceRequestInfo = null
        this.configurationInfoState!.removeRequestInfo = null
        this.configurationInfoState!.modifyRefRequestInfo = null
        break

      case 1:
        this.configurationInfoState!.newRequestInfo = null
        this.configurationInfoState!.enhanceRequestInfo = null
        this.configurationInfoState!.removeRequestInfo = null
        this.configurationInfoState!.modifyRefRequestInfo = null
        break

      case 2:
        this.configurationInfoState!.newRequestInfo = null
        this.configurationInfoState!.moveRequestInfo = null
        this.configurationInfoState!.removeRequestInfo = null
        this.configurationInfoState!.modifyRefRequestInfo = null
        break

      case 3:
        this.configurationInfoState!.newRequestInfo = null
        this.configurationInfoState!.moveRequestInfo = null
        this.configurationInfoState!.enhanceRequestInfo = null
        this.configurationInfoState!.modifyRefRequestInfo = null
        break

      case 4:
        this.configurationInfoState!.newRequestInfo = null
        this.configurationInfoState!.moveRequestInfo = null
        this.configurationInfoState!.enhanceRequestInfo = null
        this.configurationInfoState!.removeRequestInfo = null
        break
    }

    if (this.addressState && this.addressState.subCity) {
      contactService.cleanContactInfo(this.contactInfoState!)

      const files = []
      if (this.locationFilesInfoState && this.locationFilesInfoState.length > 0) {
        for (const file of this.locationFilesInfoState) {
          files.push(file.name)
        }
      }

      const data = {
        requestId: this.requestId,
        stepAddress: {
          zipCode: this.addressState.subCity.zipCode,
          subCityId: this.addressState.subCity.georesSubcityId,
          streetSubCityId: this.addressState.streetSubCity
            ? this.addressState.streetSubCity.georesStreetSubcityId
            : null,
          houseNumber: this.addressState.houseNumber,
          isMapLocation: this.addressState.isMapLocation,
          mapLocation: this.addressState.mapLocation
        },
        stepConfigurationInfo: this.configurationInfoState,
        stepAttachments: {
          attachDocuments: files.length > 0,
          files
        },
        stepContactInfo: this.contactInfoState,
        stepVatInfoSelection: {
          individualVatInfoSelection: this.individualVatInfoSelectionState,
          freelanceVatInfoSelection: this.freelanceVatInfoSelectionState,
          companyVatInfoSelection: this.companyVatInfoSelectionState
        }
      }
      const response = await this.$api.post('api/worksrequest/synergrid-connection-request', data, {
        headers: {
          'g-recaptcha-response': this.recaptcha
        }
      })
      if (response.status >= 200 && response.status < 300) {
        this.$appInsights.trackEvent('WR_SC_StepSummary_Validated', {
          Segment: 'WR_SC',
          WebRef: this.requestId,
          Remark: this.configurationInfoState!.remark !== null ? this.configurationInfoState!.remark : '',
          ConfigType: this.configurationInfoState!.changeType!.toString()
        })

        return true
      }
    }
    return false
  }
  //#endregion
}
</script>